/** The foot of every public page: where everything is, the newsletter
 *  sign-up, and the version. */
import { useState } from 'react'
import { BASE, api } from '../api'
import { Mark } from './Logo'
import { useContactEmail } from './CompanyPage'
import { useSettings, useSite } from '../settings'
import { VersionFooter } from './Version'
import { note } from './track'

export type FooterView = 'calculator' | 'compare' | 'fit' | 'chat' | 'news' | 'data' | 'privacy' | 'terms' | 'about' | 'neutrality' | 'contact' | 'assessment' | 'simulations' | 'optimise'

function Newsletter({ go }: { go: (v: FooterView) => void }) {
  const site = useSite()
  const [email, setEmail] = useState('')
  const [trap, setTrap] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [error, setError] = useState('')
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) { setError('Enter your email address first'); return }
    setState('busy'); setError('')
    try { await api.subscribe(email, trap); setState('done'); note('newsletter_signup', 'footer') }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); setState('idle') }
  }
  return (
    <div className="newsletter">
      <div className="pitch">
        <h4>Join the {site.name} newsletter</h4>
        <p>Price moves, new models and retirements across the clouds. One email each week.</p>
      </div>
      <div className="signup">
        {state === 'done' ? (
          <p className="thanks" role="status">You're subscribed. Thanks for joining.</p>
        ) : (
          <form onSubmit={submit} noValidate>
            <input type="email" value={email} placeholder="you@company.com" aria-label="Your email address" autoComplete="email"
              onChange={(e) => { setEmail(e.target.value); setError('') }} />
            {/* a field people never see or reach: a script that fills it gives itself away */}
            <input className="trap" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={trap}
              onChange={(e) => setTrap(e.target.value)} />
            <button className="primary" type="submit" disabled={state === 'busy'}>{state === 'busy' ? 'Subscribing…' : 'Subscribe'}</button>
          </form>
        )}
        {error ? <p className="err small" role="alert">{error}</p> : null}
        <p className="consent">
          By subscribing you agree to receive the {site.name} newsletter: price moves, new models and retirements, about one
          email a week. Unsubscribe any time with the link in every email. See our{' '}
          <button type="button" className="linklike" onClick={() => go('privacy')}>Privacy policy</button>.
        </p>
      </div>
    </div>
  )
}

export function SiteFooter({ go, chat, explore }: { go: (v: FooterView) => void; chat: boolean; explore?: (path: string) => void }) {
  const settings = useSettings()
  const profile = useSite()
  const site = settings.site
  const email = useContactEmail()
  const link = (v: FooterView, label: string) => <li><button type="button" className="linklike" onClick={() => go(v)}>{label}</button></li>
  return (
    <footer className="sitefoot">
      <div className="cols">
        <div className="who">
          <div className="name"><Mark className="footmark" /><span>{profile.name}</span></div>
          <p className="muted small">© {new Date().getFullYear()} {site?.operator || profile.name}</p>
          <p className="muted small">Public list prices only, as published by each cloud.</p>
        </div>
        <nav aria-label="Product">
          <h4>Product</h4>
          <ul>
            {link('calculator', 'Calculator')}{link('compare', 'Compare')}
            {chat ? link('chat', 'Ask') : null}{link('news', 'Trending')}{link('data', 'Data')}
          </ul>
        </nav>
        {explore && settings.explore?.links.length ? (
          <nav aria-label="Explore">
            <h4>Explore</h4>
            <ul>
              {settings.explore.links.map(({ path, label }) => (
                <li key={path}><a href={`${BASE}${path}`}
                  onClick={(e) => { if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) { e.preventDefault(); explore(path) } }}>{label}</a></li>
              ))}
            </ul>
          </nav>
        ) : null}
        <nav aria-label="Resources">
          <h4>Resources</h4>
          <ul>
            <li><a href={api.newsletterPdfUrl(7)}>Newsletter (PDF)</a></li>
            {link('data', 'Where the prices come from')}
            {explore && settings.explore?.live.includes('feed.xml') ? <li><a href={`${BASE}feed.xml`}>RSS feed</a></li> : null}
          </ul>
        </nav>
        <nav aria-label="Company">
          <h4>Company</h4>
          <ul>
            {link('about', 'About us')}
            {link('neutrality', 'Neutrality')}
            {link('contact', 'Contact us')}
            {email ? <li><a href={`mailto:${email}`}>{email}</a></li> : null}
          </ul>
        </nav>
        <nav aria-label="Legal">
          <h4>Legal</h4>
          <ul>
            {link('privacy', 'Privacy policy')}
            {link('terms', 'Terms and conditions')}
          </ul>
        </nav>
      </div>
      {settings.newsletter !== false ? <Newsletter go={go} /> : null}
      <div className="muted small ver" data-tour="version"><VersionFooter /></div>
    </footer>
  )
}
